import React, { useState, useEffect } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { loginUser, getCurrentUser, getAllRegisteredUsers } from '../utils/auth';

export default function ExpenseTrackerLogin() {
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDebug, setShowDebug] = useState(false);
  const [registeredUsers, setRegisteredUsers] = useState<any[]>([]);

  useEffect(() => {
    // Load registered users for debug
    const users = getAllRegisteredUsers();
    setRegisteredUsers(users);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 800)); // Simulate API call

      if (isSignup) {
        // Redirect to proper signup page
        navigate('/tools/expense-tracker/signup');
      } else {
        // Login - check if user exists with new auth system
        const user = loginUser(email, password);
        
        if (!user) {
          setError('No account found with that email. Please sign up first.');
          setLoading(false);
          return;
        }
        
        // Check if trial expired
        if (user.subscriptionStatus === 'expired') {
          setError('Your trial has expired. Please upgrade to continue.');
          setLoading(false);
          return;
        }
        
        // Redirect to expense tracker
        navigate('/tools/expense-tracker/app');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleGuestAccess = () => {
    // Allow guest access with free tier (5 expenses)
    navigate('/tools/expense-tracker/app');
  };

  return (
    <>
      <Helmet>
        <title>{isSignup ? 'Sign Up' : 'Login'} - 1099 Expense Tracker | Free1099Calc</title>
        <meta name="description" content="Sign up or login to track your 1099 business expenses with real-time audit risk scoring." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          {/* Logo & Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6 group">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-primary-600 to-primary-700 text-white text-lg font-bold shadow-lg group-hover:shadow-xl transition-shadow">
                f9
              </span>
              <span className="font-bold text-2xl text-slate-900">free1099calc</span>
            </Link>
            
            <div className="mt-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {isSignup ? 'Create Your Account' : 'Welcome Back'}
              </h1>
              <p className="text-gray-600">
                {isSignup 
                  ? 'Start tracking expenses and get your audit risk score'
                  : 'Login to access your expense tracker'
                }
              </p>
            </div>
          </div>

          {/* Login/Signup Card */}
          <div className="bg-white rounded-xl shadow-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {isSignup && (
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required={isSignup}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="••••••••"
                />
                {isSignup && (
                  <p className="mt-1 text-xs text-gray-500">
                    Minimum 8 characters
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-gradient-to-r from-primary-600 to-blue-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              >
                {loading ? 'Please wait...' : isSignup ? 'Go to Signup →' : 'Login'}
              </button>
            </form>

            {/* Toggle Login/Signup */}
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setIsSignup(!isSignup);
                  setError('');
                }}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                {isSignup ? 'Already have an account? Login' : 'Need an account? Start Free Trial'}
              </button>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or</span>
              </div>
            </div>

            {/* Guest Access */}
            <button
              onClick={handleGuestAccess}
              className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              Continue as Guest (Free Trial)
            </button>
            <p className="mt-2 text-xs text-center text-gray-500">
              Try 5 expenses free, no account required
            </p>
          </div>

          {/* Features */}
          <div className="mt-8 bg-white rounded-xl shadow-md p-6">
            <h3 className="font-semibold text-gray-900 mb-4">What You'll Get:</h3>
            <ul className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span><strong>Real-time audit risk scoring</strong> - Know which deductions are safe</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span><strong>18 IRS-compliant categories</strong> - Organize expenses correctly</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span><strong>Documentation tips</strong> - Learn what the IRS wants to see</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span><strong>$5.99/month for premium</strong> - Cancel anytime, full access</span>
              </li>
            </ul>
          </div>

          {/* Back to site */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">
              ← Back to Free1099Calc
            </Link>
          </div>

          {/* Debug Panel - DEV ONLY */}
          <div className="mt-6">
            <button
              onClick={() => setShowDebug(!showDebug)}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              {showDebug ? 'Hide' : 'Show'} Debug Info (Dev Only)
            </button>
            
            {showDebug && (
              <div className="mt-3 bg-gray-50 rounded-lg p-4 text-xs font-mono">
                <div className="font-bold mb-2">Registered Users ({registeredUsers.length}):</div>
                {registeredUsers.length === 0 ? (
                  <div className="text-gray-500">No users found in localStorage</div>
                ) : (
                  <div className="space-y-2">
                    {registeredUsers.map((user, idx) => (
                      <div key={idx} className="border-l-2 border-blue-500 pl-2">
                        <div>Email: {user.email}</div>
                        <div>Name: {user.firstName} {user.lastName}</div>
                        <div>Status: {user.subscriptionStatus}</div>
                        <div className="text-gray-500">ID: {user.id}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-gray-300">
                  <div className="font-bold mb-1">Storage Keys:</div>
                  <div>expense_tracker_users: {localStorage.getItem('expense_tracker_users') ? '✓ exists' : '✗ missing'}</div>
                  <div>expense_tracker_auth: {localStorage.getItem('expense_tracker_auth') ? '✓ exists' : '✗ missing'}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
